/**
 * Prefer a pre-generated 1k HDR sibling for editor / fast path loads.
 * Upload pipeline (or `scripts/generate-hdr-proxies`) writes `foo.1k.hdr` next to `foo.hdr`.
 * Proxies are optional — call `resolveEditorHdrUrl` so a missing `.1k.hdr` falls back
 * to the full HDR instead of hard-failing the Environment loader.
 */

const proxyOkCache = new Map<string, boolean>();

export function editorHdrProxyUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const qIdx = url.indexOf('?');
  const pathPart = qIdx >= 0 ? url.slice(0, qIdx) : url;
  const query = qIdx >= 0 ? url.slice(qIdx) : '';
  if (!/\.hdr$/i.test(pathPart)) return url;
  if (/\.1k\.hdr$/i.test(pathPart)) return url;
  return pathPart.replace(/\.hdr$/i, '.1k.hdr') + query;
}

/** Strip a `.1k` proxy suffix if present so callers can always recover the full HDR. */
export function editorHdrFullUrl(url: string): string {
  const qIdx = url.indexOf('?');
  const pathPart = qIdx >= 0 ? url.slice(0, qIdx) : url;
  const query = qIdx >= 0 ? url.slice(qIdx) : '';
  return pathPart.replace(/\.1k\.hdr$/i, '.hdr') + query;
}

/**
 * Resolve the best HDR URL for the editor: use `.1k.hdr` when it exists, else full `.hdr`.
 * Result is cached per proxy URL for the session.
 */
export async function resolveEditorHdrUrl(url: string | null | undefined): Promise<string | undefined> {
  if (!url) return undefined;
  const full = editorHdrFullUrl(url);
  const proxy = editorHdrProxyUrl(full);
  if (!proxy || proxy === full) return full;

  const cached = proxyOkCache.get(proxy);
  if (cached === true) return proxy;
  if (cached === false) return full;

  try {
    const res = await fetch(proxy, { method: 'HEAD' });
    const ok = res.ok;
    proxyOkCache.set(proxy, ok);
    return ok ? proxy : full;
  } catch {
    proxyOkCache.set(proxy, false);
    return full;
  }
}
